import { useEffect, useRef } from 'react';

let apiPromise;

function loadYouTubeApi() {
  if (window.YT?.Player) return Promise.resolve(window.YT);
  if (apiPromise) return apiPromise;

  apiPromise = new Promise((resolve, reject) => {
    const previousReady = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      resolve(window.YT);
      previousReady?.();
    };

    const existingScript = document.querySelector('script[src="https://www.youtube.com/iframe_api"]');
    const script = existingScript || document.createElement('script');
    script.addEventListener('error', () => {
      apiPromise = undefined;
      script.remove();
      window.onYouTubeIframeAPIReady = previousReady;
      reject(new Error('YouTube player API could not load'));
    }, { once: true });
    if (!existingScript) {
      script.src = 'https://www.youtube.com/iframe_api';
      script.async = true;
      document.head.appendChild(script);
    }
  });
  return apiPromise;
}

export default function YouTubeVideo({ videoId, title }) {
  const containerRef = useRef(null);

  useEffect(() => {
    const iframe = document.createElement('iframe');
    const params = new URLSearchParams({ enablejsapi: '1', origin: window.location.origin, cc_load_policy: '0' });
    iframe.src = `https://www.youtube.com/embed/${videoId}?${params}`;
    iframe.title = title;
    iframe.loading = 'lazy';
    iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
    iframe.allowFullscreen = true;
    containerRef.current.appendChild(iframe);

    let disposed = false;
    let player;
    let captionsInitialized = false;
    let playbackStarted = false;

    const clearInitialCaptions = ({ target }) => {
      if (disposed || captionsInitialized || !target.getOptions().includes('captions')) return;
      // Mark first: changing the track can itself trigger onApiChange.
      captionsInitialized = true;
      target.setOption('captions', 'track', {});
    };

    loadYouTubeApi().then(YT => {
      if (disposed) return;
      player = new YT.Player(iframe, {
        events: {
          onReady: event => {
            clearInitialCaptions(event);
            // Captions may load after onReady, even when cc_load_policy is 0.
            if (!disposed && !captionsInitialized && typeof event.target.loadModule === 'function') {
              event.target.loadModule('captions');
            }
          },
          onApiChange: clearInitialCaptions,
          onStateChange: event => {
            if (disposed || event.data !== YT.PlayerState.PLAYING || playbackStarted) return;
            playbackStarted = true;
            // YouTube can restore saved captions when playback first starts.
            if (captionsInitialized) event.target.setOption('captions', 'track', {});
            else clearInitialCaptions(event);
          },
        },
      });
    }).catch(() => {
      // Keep the ordinary iframe usable if the API script is blocked.
    });

    return () => {
      disposed = true;
      player?.destroy();
      iframe.remove();
    };
  }, [videoId, title]);

  // YouTube owns the iframe DOM so cleanup also works with React StrictMode.
  return <div ref={containerRef} />;
}
